import React, { useMemo } from "react";
import { formatHistoryDate, historyDatePresentation } from "./historyPresentation";

const number = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const display = (value, digits = 3) => {
  const parsed = number(value);
  if (parsed === null) return "Unavailable";
  return parsed.toFixed(digits).replace(/\.?0+$/, "");
};

const parseAudit = (row) => {
  const raw = row?.["Combo History Audit"];
  if (Array.isArray(raw)) return raw;
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const contributionKey = (entry, participant) => Object.keys(entry || {}).find((key) => {
  if (!key.startsWith(`Participant ${participant} `)) return false;
  return !["Game ID", "Date", "Opponent", "Minutes"].some((suffix) => key.endsWith(suffix));
});

export const comboEvidence = (row) => parseAudit(row).map((entry, index) => {
  const aKey = contributionKey(entry, 1);
  const bKey = contributionKey(entry, 2);
  const a = number(aKey ? entry[aKey] : null);
  const b = number(bKey ? entry[bKey] : null);
  const total = a !== null && b !== null ? a + b : null;
  const line = number(row?.["Prop Value"] ?? row?.Line);
  const result = total === null || line === null ? "UNAVAILABLE" : total > line ? "OVER" : total < line ? "UNDER" : "PUSH";
  return {
    rank: number(entry?.["Combo Recency Rank"]) ?? index + 1,
    a,
    b,
    total,
    result,
    aDate: entry?.["Participant 1 Date"],
    aOpponent: entry?.["Participant 1 Opponent"],
    aGameId: entry?.["Participant 1 Game ID"],
    aMinutes: number(entry?.["Participant 1 Minutes"]),
    bDate: entry?.["Participant 2 Date"],
    bOpponent: entry?.["Participant 2 Opponent"],
    bGameId: entry?.["Participant 2 Game ID"],
    bMinutes: number(entry?.["Participant 2 Minutes"]),
  };
}).filter((entry) => entry.a !== null && entry.b !== null && entry.total !== null);

const recordFor = (entries) => entries.reduce((record, entry) => {
  if (entry.result === "OVER") record.over += 1;
  if (entry.result === "UNDER") record.under += 1;
  if (entry.result === "PUSH") record.push += 1;
  return record;
}, { over: 0, under: 0, push: 0 });

const averageFor = (entries) => entries.length
  ? entries.reduce((sum, entry) => sum + entry.total, 0) / entries.length
  : null;

const lineTax = (row) => String(row?.Line_Tax_Tier ?? row?.["Line Tax Tier"] ?? "").trim() || "Unavailable";
const recommendation = (row) => String(row?.["Guru Pick"] ?? row?.Guru_Pick ?? row?.Direction ?? "Unavailable").trim();

const resultColor = (result) => result === "OVER" ? "#16A34A" : result === "UNDER" ? "#DC2626" : "#64748B";

export function ComboRecentChart({ row, sampleSize = 5, darkMode = false }) {
  const allEvidence = useMemo(() => comboEvidence(row), [row]);
  const newest = allEvidence.slice(0, Math.min(sampleSize, 5));
  const displayEntries = [...newest].reverse();
  const line = number(row?.["Prop Value"] ?? row?.Line);
  const maxValue = Math.max(1, line ?? 0, ...displayEntries.map((entry) => entry.total)) * 1.16;
  const participantA = row?.["Combo Participant 1"] || "Participant A";
  const participantB = row?.["Combo Participant 2"] || "Participant B";
  const text = darkMode ? "#E2E8F0" : "#0F172A";
  const muted = darkMode ? "#94A3B8" : "#64748B";
  const datePresentation = historyDatePresentation(
    newest.flatMap((entry) => [entry.aDate, entry.bDate].filter(Boolean).map((Date) => ({ Date }))),
    { sport: "WNBA", referenceDate: row?.GameDate || row?.Slate || new Date() }
  );

  if (!displayEntries.length) {
    return <div style={{ color: muted, fontWeight: 750 }}>Recent combined results unavailable.</div>;
  }

  return <div data-testid="wnba-combo-recent-chart" role="group" aria-label="Five most recent results in chronological order; newest result on the right.">
    <div style={{ overflowX: "auto", overflowY: "hidden", paddingTop: 24 }}>
      <div style={{ minWidth: 420, height: 250, position: "relative", padding: "0 16px 34px" }}>
        {line !== null && <div style={{ position: "absolute", zIndex: 3, left: 8, right: 8, bottom: `calc(34px + ${(line / maxValue) * 216}px)`, borderTop: "2px dashed #475569" }}>
          <span style={{ position: "absolute", right: 0, top: -22, padding: "2px 7px", borderRadius: 999, background: darkMode ? "#1E293B" : "#FFFFFF", color: text, fontSize: 11, fontWeight: 900 }}>Line {display(line, 1)}</span>
        </div>}
        <div style={{ height: 216, display: "grid", gridTemplateColumns: `repeat(${displayEntries.length}, minmax(46px, 1fr))`, gap: 10, alignItems: "end" }}>
          {displayEntries.map((entry, index) => {
            const height = Math.max(3, (entry.total / maxValue) * 100);
            const aDate = formatHistoryDate(entry.aDate, { includeYear: datePresentation.includeYear });
            const bDate = formatHistoryDate(entry.bDate, { includeYear: datePresentation.includeYear });
            const label = aDate === bDate ? aDate : `${aDate} / ${bDate}`;
            const title = `${participantA}: ${display(entry.a)} • ${participantB}: ${display(entry.b)} • Total: ${display(entry.total)} • Line: ${display(line, 1)} • ${entry.result}`;
            return <div key={`combo-result-${entry.rank}`} title={title} style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "flex-end", minWidth: 0, position: "relative" }}>
              <div style={{ height: `${height}%`, minHeight: 12, position: "relative", border: `2px solid ${resultColor(entry.result)}`, borderRadius: "7px 7px 2px 2px", display: "flex", flexDirection: "column-reverse", overflow: "visible", background: darkMode ? "#0F172A" : "#F8FAFC" }}>
                <div style={{ height: `${(entry.a / entry.total) * 100}%`, background: "#2563EB", borderRadius: "0 0 4px 4px" }} />
                <div style={{ height: `${(entry.b / entry.total) * 100}%`, background: "#06B6D4", borderRadius: "4px 4px 0 0" }} />
                <div style={{ position: "absolute", left: "50%", top: -21, transform: "translateX(-50%)", color: resultColor(entry.result), fontWeight: 950, fontSize: 12, whiteSpace: "nowrap" }}>{display(entry.total)}</div>
              </div>
              <div style={{ position: "absolute", bottom: -30, left: 0, width: "100%", color: entry.rank === 1 ? text : muted, fontSize: 10, fontWeight: entry.rank === 1 ? 950 : 800, textAlign: "center", whiteSpace: "normal" }}>{label}</div>
            </div>;
          })}
        </div>
      </div>
    </div>
    <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 8, color: muted, fontSize: 13, fontWeight: 850 }}>
      <span><i style={{ display: "inline-block", width: 9, height: 9, background: "#2563EB", marginRight: 5 }} />{participantA}</span>
      <span><i style={{ display: "inline-block", width: 9, height: 9, background: "#06B6D4", marginRight: 5 }} />{participantB}</span>
    </div>
  </div>;
}

function SummaryTile({ label, value, darkMode }) {
  return <div style={{ border: `1px solid ${darkMode ? "#334155" : "#E2E8F0"}`, borderRadius: 12, padding: "10px 12px", background: darkMode ? "#111827" : "#FFFFFF", minWidth: 0 }}>
    <div style={{ color: darkMode ? "#94A3B8" : "#64748B", fontSize: 11, fontWeight: 850 }}>{label}</div>
    <div style={{ color: darkMode ? "#F8FAFC" : "#0F172A", fontSize: 16, fontWeight: 950, overflowWrap: "anywhere" }}>{value || "Unavailable"}</div>
  </div>;
}

export function WnbaCustomerComboDetails({ row, darkMode = false }) {
  const evidence = useMemo(() => comboEvidence(row), [row]);
  const sample = evidence.slice(0, 5);
  const record = recordFor(sample);
  const average = averageFor(sample);
  const pick = recommendation(row).toUpperCase();
  const supported = record.over > record.under ? "OVER" : record.under > record.over ? "UNDER" : "MIXED";
  const estimate = pick === "UNDER" ? row?.PWG_Prob_Under : row?.PWG_Prob_Over;
  const edge = number(row?.DELTA);
  const background = darkMode ? "#0F172A" : "#F8FAFC";
  const text = darkMode ? "#F8FAFC" : "#0F172A";
  const muted = darkMode ? "#94A3B8" : "#64748B";
  return <section data-testid="wnba-customer-combo-details" style={{ padding: 16, color: text }}>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))", gap: 10 }}>
      <SummaryTile label="Prop and line" value={`${row?.["Prop Type"] || "Combined prop"} ${display(row?.["Prop Value"] ?? row?.Line, 1)}`} darkMode={darkMode} />
      <SummaryTile label="Estimate" value={`${display(estimate, 1)}% ${pick}`} darkMode={darkMode} />
      <SummaryTile label="Risk" value={row?.Risk_Tier ?? row?.["Risk Tier"]} darkMode={darkMode} />
      <SummaryTile label="Conviction" value={row?.PWG_Confidence_Level ?? row?.Confidence} darkMode={darkMode} />
    </div>
    <div style={{ marginTop: 14, border: `1px solid ${darkMode ? "#334155" : "#CBD5E1"}`, borderRadius: 14, padding: 14, background }}>
      <div style={{ fontWeight: 950, marginBottom: 10 }}>Combined projection</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))", gap: 9 }}>
        <SummaryTile label={row?.["Combo Participant 1"] || "Participant A"} value={display(row?.["Combo Participant 1 Projection"])} darkMode={darkMode} />
        <SummaryTile label={row?.["Combo Participant 2"] || "Participant B"} value={display(row?.["Combo Participant 2 Projection"])} darkMode={darkMode} />
        <SummaryTile label="PWG combined projection" value={display(row?.["Combo Combined Projection"] ?? row?.Proj)} darkMode={darkMode} />
        <SummaryTile label="Difference from line" value={edge === null ? "Unavailable" : `${edge >= 0 ? "+" : ""}${display(edge)}`} darkMode={darkMode} />
        <SummaryTile label="Line Tax" value={lineTax(row)} darkMode={darkMode} />
      </div>
    </div>
    <div style={{ marginTop: 14, border: `1px solid ${darkMode ? "#334155" : "#CBD5E1"}`, borderRadius: 14, padding: 14, background }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div style={{ fontWeight: 950 }}>Recent Combined Results</div>
      </div>
      <ComboRecentChart row={row} sampleSize={5} darkMode={darkMode} />
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 10, color: muted, fontSize: 13, fontWeight: 850 }}>
        <span>{pick === "OVER" ? "OVER" : "UNDER"}: {pick === "OVER" ? record.over : record.under} of {sample.length}</span>
        <span>Combined average: {display(average, 1)}</span>
        <span>Recent results support: {supported}</span>
      </div>
    </div>
  </section>;
}

export function WnbaAdminComboDetails({ row, darkMode = false, narrow = false }) {
  const evidence = useMemo(() => comboEvidence(row), [row]);
  const l5 = evidence.slice(0, 5);
  const l10 = evidence.slice(0, 10);
  const l5Record = recordFor(l5);
  const l10Record = recordFor(l10);
  const edge = number(row?.DELTA);
  const items = [
    [row?.["Combo Participant 1"] || "Participant A", display(row?.["Combo Participant 1 Projection"])],
    [row?.["Combo Participant 2"] || "Participant B", display(row?.["Combo Participant 2 Projection"])],
    ["Combined projection", display(row?.["Combo Combined Projection"] ?? row?.Proj)], ["Combo line", display(row?.["Prop Value"] ?? row?.Line, 1)],
    ["Edge", edge === null ? "Unavailable" : `${edge >= 0 ? "+" : ""}${display(edge)}`], ["Estimated OVER", `${display(row?.PWG_Prob_Over, 1)}%`],
    ["Estimated UNDER", `${display(row?.PWG_Prob_Under, 1)}%`], ["Recommendation", recommendation(row)], ["Tag", row?.Display_Tag ?? row?.Tag],
    ["Line Tax", lineTax(row)], ["Risk", row?.Risk_Tier ?? row?.["Risk Tier"]], ["Confidence", row?.PWG_Confidence_Level ?? row?.Confidence], ["DQS", display(row?.DQS ?? row?.dqs, 2)],
    ["L5 average and record", `${display(averageFor(l5), 1)} • O ${l5Record.over} / U ${l5Record.under} / P ${l5Record.push}`],
    ["L10 average and record", `${display(averageFor(l10), 1)} • O ${l10Record.over} / U ${l10Record.under} / P ${l10Record.push}`],
    ["L5 combined values", l5.map((entry) => display(entry.total)).join(", ")], ["L10 combined values", l10.map((entry) => display(entry.total)).join(", ")],
  ];
  const cell = { padding: "7px 8px", borderBottom: `1px solid ${darkMode ? "#334155" : "#E2E8F0"}`, verticalAlign: "top" };
  return <section data-testid="wnba-admin-combo-details" style={{ padding: 16 }}>
    <h3 style={{ margin: "0 0 6px" }}>Combined Prop Analysis</h3>
    <p style={{ margin: "0 0 12px", color: darkMode ? "#CBD5E1" : "#475569", fontSize: 13, fontWeight: 750 }}>Each participant’s recent performances are aligned independently from newest to oldest. They are not shared historical games.</p>
    <div style={{ display: "grid", gridTemplateColumns: narrow ? "1fr 1fr" : "repeat(4, minmax(0, 1fr))", gap: 8 }}>{items.map(([label, value]) => <SummaryTile key={label} label={label} value={value} darkMode={darkMode} />)}</div>
    <details style={{ marginTop: 14 }}><summary style={{ cursor: "pointer", fontWeight: 950 }}>Combined history audit</summary>
      <div style={{ overflowX: "auto", marginTop: 8 }}><table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, minWidth: 1250 }}>
        <thead><tr>{["Position", "Participant A", "A date", "A opponent", "A Game ID", "A minutes", "Participant B", "B date", "B opponent", "B Game ID", "B minutes", "Combined", "Line", "Result"].map((label) => <th key={label} style={{ ...cell, textAlign: "left" }}>{label}</th>)}</tr></thead>
        <tbody>{evidence.map((entry) => <tr key={`audit-${entry.rank}`}><td style={cell}>{entry.rank}</td><td style={cell}>{display(entry.a)}</td><td style={cell}>{entry.aDate || "Unavailable"}</td><td style={cell}>{entry.aOpponent || "Unavailable"}</td><td style={cell}>{entry.aGameId || "Unavailable"}</td><td style={cell}>{display(entry.aMinutes, 1)}</td><td style={cell}>{display(entry.b)}</td><td style={cell}>{entry.bDate || "Unavailable"}</td><td style={cell}>{entry.bOpponent || "Unavailable"}</td><td style={cell}>{entry.bGameId || "Unavailable"}</td><td style={cell}>{display(entry.bMinutes, 1)}</td><td style={{ ...cell, fontWeight: 950 }}>{display(entry.total)}</td><td style={cell}>{display(row?.["Prop Value"] ?? row?.Line, 1)}</td><td style={{ ...cell, color: resultColor(entry.result), fontWeight: 950 }}>{entry.result}</td></tr>)}</tbody>
      </table></div>
    </details>
  </section>;
}

export default function WnbaComboDetails({ row, isAdmin = false, darkMode = false, narrow = false }) {
  return isAdmin
    ? <WnbaAdminComboDetails row={row} darkMode={darkMode} narrow={narrow} />
    : <WnbaCustomerComboDetails row={row} darkMode={darkMode} />;
}
